import { useEffect, useState } from "react";
import { getMyProfile, requestFleetOwnerAccess, getMyFleet } from "../../api/client";
import BottomNav from "../../components/BottomNav";
import UserPageHeader from "../../components/UserPageHeader";
import ApplicationStatusCard from "../../components/ApplicationStatusCard";
import LoadingIndicator from "../../components/LoadingIndicator";

function naira(n) {
  return `₦${Math.round(n || 0).toLocaleString()}`;
}

export default function FleetDashboard() {
  const [profile, setProfile] = useState(null);
  const [fleet, setFleet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [fleetName, setFleetName] = useState("");
  const [requestBusy, setRequestBusy] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");
  const [requestError, setRequestError] = useState("");

  useEffect(() => {
    getMyProfile()
      .then(async r => {
        setProfile(r.data);
        if (r.data.fleetOwnerApprovalStatus === "approved") {
          const { data } = await getMyFleet();
          setFleet(data);
        }
      })
      .catch(err => setError(err.response?.data?.error || "Could not load your fleet."))
      .finally(() => setLoading(false));
  }, []);

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setRequestError("");
    setRequestMessage("");
    setRequestBusy(true);
    try {
      const { data } = await requestFleetOwnerAccess(fleetName);
      setRequestMessage("✓ Fleet Owner access request submitted — an admin will review and approve your account.");
      setProfile(p => ({ ...p, fleetOwnerApprovalStatus: data.fleetOwnerApprovalStatus }));
    } catch (err) {
      setRequestError(err.response?.data?.error || "Could not submit Fleet Owner access request.");
    } finally {
      setRequestBusy(false);
    }
  };

  return (
    <div className="user-app">
      <BottomNav />
      <div className="user-app-main">
        <UserPageHeader backTo="/business" backLabel="Businesses" />
        <div className="page wide">

          <div className="brand-header">
            <span className="brand-logo">🚚</span>
            <span className="brand-name">{fleet?.fleetName || "Fleet Owner"}</span>
            <p className="brand-tagline">Own multiple dispatchers? Get a rollup view of your fleet's jobs and earnings.</p>
          </div>

          {error && <p className="error">{error}</p>}

          {loading ? (
            <LoadingIndicator />
          ) : profile?.fleetOwnerApprovalStatus === "pending" ? (
            <ApplicationStatusCard
              status="pending"
              title="Application Under Review"
              description="Your Fleet Owner access request is being reviewed by our admin team. You'll have access once approved."
            />
          ) : profile?.fleetOwnerApprovalStatus === "revoked" ? (
            <ApplicationStatusCard
              status="revoked"
              title="Fleet Owner Access Revoked"
              description="Your Fleet Owner access was revoked by an administrator. Please contact support for more information."
            />
          ) : profile?.fleetOwnerApprovalStatus === "approved" && fleet ? (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", marginBottom: "24px" }}>
                <div className="delivery-card" style={{ flex: "1 1 180px" }}>
                  <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>Fleet Size</div>
                  <div style={{ fontSize: "26px", fontWeight: 800, marginTop: "4px" }}>{fleet.summary.fleetSize}</div>
                </div>
                <div className="delivery-card" style={{ flex: "1 1 180px" }}>
                  <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>Online Now</div>
                  <div style={{ fontSize: "26px", fontWeight: 800, marginTop: "4px", color: "var(--success-hover)" }}>{fleet.summary.onlineCount}</div>
                </div>
                <div className="delivery-card" style={{ flex: "1 1 180px" }}>
                  <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>Digital Wallets</div>
                  <div style={{ fontSize: "26px", fontWeight: 800, marginTop: "4px", color: "var(--info-hover)" }}>{naira(fleet.summary.totalDigitalWallet)}</div>
                </div>
                <div className="delivery-card" style={{ flex: "1 1 180px" }}>
                  <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>Cash Owed</div>
                  <div style={{ fontSize: "26px", fontWeight: 800, marginTop: "4px", color: "var(--warning-hover)" }}>{naira(fleet.summary.totalCashOwed)}</div>
                </div>
              </div>

              <div className="delivery-card">
                <h3>Dispatchers</h3>
                {fleet.riders.length === 0 ? (
                  <p className="hint">No dispatchers linked to your fleet yet — ask support to link them.</p>
                ) : (
                  fleet.riders.map(r => (
                    <div key={r._id} className="history-row">
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <strong>{r.name}</strong>
                          <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                            {r.phone} · {r.vehicleType?.toUpperCase()} · {r.city}
                            {" · "}
                            <span style={{ color: r.isOnline ? "var(--success-hover)" : "var(--text-muted)" }}>{r.isOnline ? "🟢 Online" : "⚫ Offline"}</span>
                            {" · ⭐ "}{r.rating?.toFixed(1) || "5.0"}
                          </p>
                        </div>
                        <div style={{ textAlign: "right", fontSize: "13px" }}>
                          <div style={{ color: "var(--info-hover)" }}>Wallet: {naira(r.wallet.earningsBalance)}</div>
                          <div style={{ color: "var(--warning-hover)" }}>Owed: {naira(r.wallet.cashOwedBalance)}</div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="delivery-card" style={{ maxWidth: "520px" }}>
              <p className="hint" style={{ marginBottom: "12px" }}>
                Own multiple dispatchers? Get a rollup view of your fleet's jobs and earnings.
                Once approved, ask support to link your dispatcher accounts to your fleet.
              </p>
              <form onSubmit={handleRequestSubmit}>
                <div className="form-field">
                  <label>Fleet / Business Name</label>
                  <input type="text" placeholder="e.g. Adeyemi Logistics Fleet" value={fleetName} onChange={e => setFleetName(e.target.value)} required />
                </div>
                {requestError && <p className="error">{requestError}</p>}
                {requestMessage && <p style={{ color: "var(--success)", fontSize: "14px", marginBottom: "8px" }}>{requestMessage}</p>}
                <button type="submit" disabled={requestBusy}>{requestBusy ? "Submitting..." : "Request Fleet Owner Access"}</button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
